import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { WaiverFormRule, WaiverFormSchema } from "@factory/form/form.rule";
import { useFormDefaultWaiver } from "@factory/form/form-default-waiver.hook";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { 
  useQyUpdateWaiver, 
  useQyGetWaiverById, 
  useQyDeleteWaiver 
} from "@factory/query/waiver.query";
import { GetResponseWaiverModel } from "@factory/model/query-model-waiver";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";

export function useEditWaiver() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultWaiver();
  const navigate = useNavigate();
  const { mapUpdateWaiver, mapDeleteWaiver } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { waiverId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Waiver
  const handleGetApiSuccess = (data: GetResponseWaiverModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("tenant", responseData?.tenant || "");
      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
      setValue("fontSize", responseData?.font_size);
      setValue("subTitle", responseData?.sub_title || "");
  };
  const {
    data: waiverResponse,
    isLoading,
    isError: waiverError,
  } = useQyGetWaiverById(waiverId || "", handleGetApiSuccess);
  const waiverData = waiverResponse?.data?.[0];

  // API UPDATE Waiver
  const handleUpdateApiSuccess = () => {
    showSuccess("Waiver updated successfully");
  };
  const { mutate: updateWaiver, isLoading: isEditLoading } = useQyUpdateWaiver(
    handleUpdateApiSuccess
  );

  // API DELETE Waiver
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Waiver deleted successfully");
  };
  const { mutate: deleteWaiver } = useQyDeleteWaiver(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<WaiverFormSchema>({
    defaultValues: getDefault(waiverData),
    resolver: zodResolver(WaiverFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: WaiverFormSchema) => {
    if (!waiverData || !waiverData.code) {
      throw new Error("No waiver data");
    }

    const formData = mapUpdateWaiver(form, waiverData.code);
    updateWaiver(formData);
  };
  const handleValidateError = (err: FieldErrors<WaiverFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!waiverData || !waiverData.code) {
      throw new Error("No waiver data found");
    }

    const form = mapDeleteWaiver(waiverData.code);
    deleteWaiver(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    waiverId,
    waiverData,
    isLoading,
    waiverError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}